import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicRequest, userRequest } from '../../utils/requestMethods';

const getField = (fields, name) => fields?.find(f => f.name === name)?.value || '';

const formatDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d) ? raw : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

const Detail = ({ label, value }) =>
  value ? (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</span>
      <span className="text-sm text-gray-700 font-medium">{value}</span>
    </div>
  ) : null;

const Initials = ({ name }) => {
  const parts = name.trim().split(' ');
  const letters = parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`
    : name.slice(0, 2);
  return (
    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
      <span className="text-indigo-700 font-bold text-sm uppercase">{letters}</span>
    </div>
  );
};

const SubmissionCard = ({ submission }) => {
  const firstName = getField(submission.fields, 'child_first_name');
  const lastName = getField(submission.fields, 'child_last_name');
  const fullName = `${firstName} ${lastName}`.trim() || 'N/A';
  const gender = getField(submission.fields, 'gender') || getField(submission.fields, 'child_gender');
  const dob = getField(submission.fields, 'dob') || getField(submission.fields, 'child_date_of_birth');
  const nationality = getField(submission.fields, 'nationality');
  const className = submission.class?.class_name;
  const submittedDate = formatDate(submission.createdAt);

  const isApproved = submission.status === 'approved';
  const hasCandidateNumber = !!submission.candidate_number;

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden shadow-sm ${isApproved ? 'border-gray-200' : 'border-amber-200'}`}>
      {/* Card header */}
      <div className={`px-6 py-4 flex items-center justify-between gap-4 ${isApproved ? 'border-b border-gray-100' : 'border-b border-amber-100 bg-amber-50/40'}`}>
        <div className="flex items-center gap-3 min-w-0">
          <Initials name={fullName} />
          <div className="min-w-0">
            <p className="font-bold text-gray-900 text-base leading-tight truncate">{fullName}</p>
            {className && (
              <p className="text-sm text-gray-500 mt-0.5">
                Applied for <span className="font-semibold text-gray-700">{className}</span>
              </p>
            )}
          </div>
        </div>
        {isApproved ? (
          <span className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-bold rounded-lg">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Approved
          </span>
        ) : (
          <span className="flex-shrink-0 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold rounded-lg">
            Pending
          </span>
        )}
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Candidate number — assigned at submission */}
        {hasCandidateNumber && (
          <div className="flex items-center justify-between gap-4 p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-400 mb-1">
                Candidate Number
              </p>
              <p className="text-2xl font-black font-mono tracking-wider text-indigo-700 leading-none">
                {submission.candidate_number}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
              </svg>
            </div>
          </div>
        )}

        {!isApproved && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl">
            <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 00-3.42 0z" />
            </svg>
            <p className="text-sm text-amber-700">
              This application is currently under review by the school.
            </p>
          </div>
        )}

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
          <Detail label="Gender" value={gender} />
          <Detail label="Date of Birth" value={formatDate(dob)} />
          <Detail label="Nationality" value={nationality} />
          <Detail label="Date Submitted" value={submittedDate} />
        </div>
      </div>
    </div>
  );
};

const SubmissionStatus = () => {
  const { schoolSlug, userEmail } = useParams();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const decodedEmail = decodeURIComponent(userEmail);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        const { data } = await publicRequest.get(
          `/schools/${schoolSlug}/enrol-submissions/${decodedEmail}`
        );
        setSubmissions(data.submissions || []);
      } catch {
        setError('Could not load submissions. Please check the link or contact the school.');
      } finally {
        setLoading(false);
      }
    };

    if (schoolSlug && userEmail) fetchSubmissions();
  }, [schoolSlug, userEmail]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-600 border-t-transparent" />
        <p className="text-sm text-gray-400">Loading submissions...</p>
      </div>
    );
  }

  if (error || submissions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3 px-4 text-center">
        <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
          <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        </div>
        <p className="font-semibold text-gray-700">No submissions found</p>
        <p className="text-sm text-gray-400 max-w-sm">
          {error || 'No enrollment submissions were found for this email address.'}
        </p>
      </div>
    );
  }

  const schoolName = submissions[0]?.school?.school_name || '';
  const approvedCount = submissions.filter(s => s.status === 'approved').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 py-6">
        <div className="max-w-xl mx-auto">
          {schoolName && (
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-1">
              {schoolName}
            </p>
          )}
          <h1 className="text-xl font-bold text-gray-900">Enrollment Applications</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {decodedEmail} · {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
            {approvedCount > 0 && (
              <span className="text-green-600 font-medium"> · {approvedCount} approved</span>
            )}
          </p>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6 space-y-4">
        {submissions.map(submission => (
          <SubmissionCard key={submission._id} submission={submission} />
        ))}
      </div>
    </div>
  );
};

export default SubmissionStatus;
